import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import useTeamMembers from '../hooks/useTeamMembers';
import { AVAILABILITY_STATUSES, SPEAKER_TYPES } from '../labels';
import type { AvailabilityStatus, SpeakerProfile, SpeakerProfileInput, SpeakerType } from '../types';

interface FormState {
  display_name: string;
  speaker_type: SpeakerType;
  biography: string;
  availability_status: AvailabilityStatus;
  user_id: string;
}

const EMPTY_FORM: FormState = {
  display_name: '', speaker_type: 'intern', biography: '', availability_status: 'beschikbaar', user_id: '',
};

function toFormState(profile: SpeakerProfile | null): FormState {
  if (!profile) return EMPTY_FORM;
  return {
    display_name: profile.display_name,
    speaker_type: profile.speaker_type,
    biography: profile.biography || '',
    availability_status: profile.availability_status,
    user_id: profile.user_id || '',
  };
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profile: SpeakerProfile | null;
  onSubmit: (data: SpeakerProfileInput) => Promise<void>;
}

export default function ProfileFormDialog({ open, onOpenChange, profile, onSubmit }: Props) {
  const teamMembers = useTeamMembers();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setForm(toFormState(profile));
  }, [open, profile]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.display_name.trim()) return;
    setSubmitting(true);
    try {
      await onSubmit({
        display_name: form.display_name.trim(),
        speaker_type: form.speaker_type,
        biography: form.biography || null,
        availability_status: form.availability_status,
        user_id: form.speaker_type === 'intern' ? form.user_id || null : null,
      });
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{profile ? 'Sprekersprofiel bewerken' : 'Nieuw sprekersprofiel'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="profile-name">Naam</Label>
            <Input
              id="profile-name"
              value={form.display_name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, display_name: e.target.value })}
              required
            />
          </div>

          <div>
            <Label htmlFor="profile-type">Type</Label>
            <Select value={form.speaker_type} onValueChange={(value: SpeakerType) => setForm({ ...form, speaker_type: value })}>
              <SelectTrigger id="profile-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SPEAKER_TYPES.map((option) => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {form.speaker_type === 'intern' && (
            <div>
              <Label htmlFor="profile-user">Gekoppelde gebruiker</Label>
              <Select value={form.user_id || undefined} onValueChange={(value: string) => setForm({ ...form, user_id: value })}>
                <SelectTrigger id="profile-user"><SelectValue placeholder="Kies iemand" /></SelectTrigger>
                <SelectContent>
                  {teamMembers.map((member) => (
                    <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label htmlFor="profile-availability">Beschikbaarheid</Label>
            <Select
              value={form.availability_status}
              onValueChange={(value: AvailabilityStatus) => setForm({ ...form, availability_status: value })}
            >
              <SelectTrigger id="profile-availability"><SelectValue /></SelectTrigger>
              <SelectContent>
                {AVAILABILITY_STATUSES.map((option) => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="profile-bio">Korte biografie</Label>
            <Textarea
              id="profile-bio"
              value={form.biography}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setForm({ ...form, biography: e.target.value })}
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuleren</Button>
            <Button type="submit" disabled={submitting || !form.display_name.trim()}>
              {profile ? 'Opslaan' : 'Aanmaken'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
